import React, { useCallback, useState } from 'react';

import { apiPatchAsUser } from '../../../lib/api_v2';
import { logError } from '../../../lib/logger';
import { cn } from '../../../lib/utils';

import { FIELD_INPUT_CLASS, FIELD_INPUT_ERROR_CLASS, FIELD_LABEL_CLASS, FIELD_SUBMIT_BUTTON_CLASS, firstFieldError, responseValidations } from './accountStyles';

const EMPTY_FORM = { current_password: '', password: '', password_confirmation: '' };

const AccountPasswordPanel = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges = form.current_password.length > 0 && form.password.length > 0 && form.password_confirmation.length > 0;
  const isDisabled = isSaving || !hasChanges;
  const currentPasswordError = firstFieldError(errors.current_password);
  const passwordError = firstFieldError(errors.password);
  const passwordConfirmationError = firstFieldError(errors.password_confirmation);

  const handleFieldChange = useCallback(function handleAccountPasswordFieldChange(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setStatus(null);
    setErrors((prev) => ({ ...prev, [name]: null, general: null }));
  }, []);

  const handleSubmit = useCallback(
    async function submitAccountPassword(event) {
      event.preventDefault();
      if (isSaving || !hasChanges) return;

      setIsSaving(true);
      setStatus(null);
      setErrors({});

      try {
        const response = await apiPatchAsUser('/account/password', {
          user: {
            current_password: form.current_password,
            password: form.password,
            password_confirmation: form.password_confirmation,
          },
        });
        const data = await response.json();

        if (!response.ok || data.ok !== true) {
          setErrors(responseValidations(data) || { general: 'Unable to update password.' });
          return;
        }

        setForm(EMPTY_FORM);
        setStatus('Saved');
      } catch (saveError) {
        logError('AccountPasswordPanel', 'Failed to update password', { error: saveError });
        setErrors({ general: 'Unable to update password.' });
      } finally {
        setIsSaving(false);
      }
    },
    [form, hasChanges, isSaving],
  );

  return (
    <form onSubmit={handleSubmit} className="w-full">
      {errors.general && (
        <div className="mb-5 rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{errors.general}</div>
      )}

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label htmlFor="account_current_password" className={FIELD_LABEL_CLASS}>
            Current password
          </label>
          <input
            id="account_current_password"
            name="current_password"
            type="password"
            autoComplete="current-password"
            value={form.current_password}
            onChange={handleFieldChange}
            placeholder="Current password"
            className={cn(FIELD_INPUT_CLASS, currentPasswordError && FIELD_INPUT_ERROR_CLASS)}
          />
          {currentPasswordError && <span className="text-xs text-red-400">{currentPasswordError}</span>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="account_new_password" className={FIELD_LABEL_CLASS}>
            New password
          </label>
          <input
            id="account_new_password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={handleFieldChange}
            placeholder="New password"
            className={cn(FIELD_INPUT_CLASS, passwordError && FIELD_INPUT_ERROR_CLASS)}
          />
          {passwordError && <span className="text-xs text-red-400">{passwordError}</span>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="account_password_confirmation" className={FIELD_LABEL_CLASS}>
            Confirm new password
          </label>
          <input
            id="account_password_confirmation"
            name="password_confirmation"
            type="password"
            autoComplete="new-password"
            value={form.password_confirmation}
            onChange={handleFieldChange}
            placeholder="Confirm new password"
            className={cn(FIELD_INPUT_CLASS, passwordConfirmationError && FIELD_INPUT_ERROR_CLASS)}
          />
          {passwordConfirmationError && <span className="text-xs text-red-400">{passwordConfirmationError}</span>}
        </div>

        <div className="flex items-center justify-end gap-3">
          <span className="min-h-5 text-xs text-gray-500 dark:text-neutral-400" aria-live="polite">
            {isSaving ? 'Saving...' : status}
          </span>
          <button type="submit" className={FIELD_SUBMIT_BUTTON_CLASS} disabled={isDisabled}>
            Update
          </button>
        </div>
      </div>
    </form>
  );
};

export default AccountPasswordPanel;
